import { cn } from '@/lib/utils'
import type { PriceDisplay } from '@/lib/price-display'

interface SalePriceProps {
  /**
   * Qué pintar, ya resuelto (`lib/price-display.ts`). El componente NO decide si
   * hay rebaja ni de cuánto: desde que el paywall pinta lo que va a cobrar el
   * proveedor y las landings siguen con el catálogo editorial, hay dos políticas
   * de rebaja distintas y ninguna es asunto de la maquetación.
   */
  display: PriceDisplay
  /**
   * Clases del precio DESTACADO (el rebajado cuando hay descuento, o el único
   * número cuando no lo hay). Pasa las clases existentes de cada call site para
   * no cambiar el aspecto. P.ej. "text-3xl font-bold text-amber-300 drop-shadow-md".
   */
  priceClassName: string
  /**
   * Clases de color del badge. Por defecto el tratamiento gold/accent (promo).
   * Permite alinear con la paleta de badge por variante en landing-pricing.
   */
  badgeClassName?: string
  /** Clases extra para el wrapper flex (p.ej. "justify-end"). */
  className?: string
}

/**
 * Precio presentacional. Con rebaja muestra el precio original tachado, el
 * rebajado destacado y un badge "-X%"; sin ella renderiza exactamente el precio
 * destacado solo (sin cambios visuales).
 *
 * Solo presentacional: el cobro real lo gestiona DodoPayments. De dónde sale
 * cada número lo deciden `lib/price-display.ts` y sus llamantes.
 */
export function SalePrice({
  display,
  priceClassName,
  badgeClassName = 'bg-amber-400 text-amber-900',
  className,
}: SalePriceProps) {
  if (display.kind === 'plain') {
    return <span className={priceClassName}>{display.price}</span>
  }

  return (
    <span className={cn('flex flex-wrap items-baseline gap-x-2 gap-y-1', className)}>
      <span className="text-white/70 line-through text-xl sm:text-2xl font-semibold drop-shadow-md">
        {display.originalPrice}
      </span>
      <span className={priceClassName}>{display.price}</span>
      <span
        className={cn('rounded-full px-2.5 py-1 text-xl font-bold shadow-md', badgeClassName)}
      >
        {display.discountLabel}
      </span>
    </span>
  )
}
